import { Checkbox } from '@sparefoot/react-core';
import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { compose } from 'redux';
import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import { on, off } from '@sparefoot/react-core/utils/dom';
import { withShowHide } from '@sparefoot/react-core/decorators';

import { Link } from 'components/core/Link';
import CaretDown from 'components/svg/CaretDown';
import { breakpointsByName } from 'config/breakpoints';
import { COLOR_BLUE, COLOR_SECONDARY_BLUE } from 'config/colors';

import GeoSpecificMenu from './GeoSpecificMenu/GeoSpecificMenu';
import DefaultMenu from './DefaultMenu/DefaultMenu';

export const enhance = compose(
	withShowHide('findStorage'),
	withShowHide('dropdown'),
	withShowHide('owners'),
);

/**
 * Returns a safe href for use in Link components.
 * Only allows root-relative paths or https: URLs to prevent javascript:, data:, or protocol-relative URLs.
 *
 * Security behavior:
 * - javascript:, data:, and other non-https protocols are not allowed as schemes; strings that look
 *   like them are treated as path segments and normalized with a leading slash (e.g. "/javascript:...").
 * - Protocol-relative URLs (e.g. "//evil.com") are rejected and return '#'.
 * - Paths without a leading slash (e.g. "self-storage/texas/del-valle/") are normalized to
 *   root-relative by prepending '/'. This is intentional so that backend nearbyCities.uri values
 *   that omit the leading slash still produce navigable links; if the backend only ever sends
 *   paths with leading slashes, unexpected bare paths could be treated as invalid and return '#'
 *   for a stricter fail-safe policy.
 *
 * @param {string} uri - A path (e.g. "/self-storage/texas/del-valle/" or "self-storage/texas/del-valle/") or https URL.
 * @returns {string} The URI if safe; otherwise '#'.
 */
export const getSafeHref = (uri) => {
	if (typeof uri !== 'string') {
		return '#';
	}
	const trimmed = uri.trim();
	if (!trimmed) {
		return '#';
	}
	if (trimmed.toLowerCase().startsWith('https://')) {
		return trimmed;
	}
	// Reject protocol-relative URLs (e.g. "//evil.com") which could load external content
	if (trimmed.startsWith('//')) {
		return '#';
	}
	if (trimmed.startsWith('/')) {
		return trimmed;
	}
	// Path without leading slash – normalize to root-relative
	return `/${trimmed}`;
};

export const getFindStorageNearUserLinks = (nearbyCities = []) => {
	return nearbyCities.slice(0, 5).map(({ city, state, uri }) => ({
		label: `Self Storage in ${city}, ${state}`,
		href: getSafeHref(uri),
	}));
};

export const isDrillDownLinksPresent = (drillDownLinks) => {
	if (!drillDownLinks) {
		return false;
	}
	return Object.keys(drillDownLinks).some(
		(key) => drillDownLinks[key] && drillDownLinks[key].length > 0,
	);
};

const FIND_STORAGE_TOP_CITIES_LINKS = [
	{
		label: 'Self Storage in Atlanta, GA',
		href: '/self-storage/georgia/atlanta/',
	},
	{
		label: 'Self Storage in Boston, MA',
		href: '/self-storage/massachusetts/boston/',
	},
	{
		label: 'Self Storage in Dallas, TX',
		href: '/self-storage/texas/dallas/',
	},
	{
		label: 'Self Storage in Denver, CO',
		href: '/self-storage/colorado/denver/',
	},
	{
		label: 'Self Storage in Las Vegas, NV',
		href: '/self-storage/nevada/las-vegas/',
	},
	{
		label: 'Self Storage in Louisville, KY',
		href: '/self-storage/kentucky/louisville/',
	},
	{
		label: 'Self Storage in Los Angeles, CA',
		href: '/self-storage/california/los-angeles/',
	},
	{
		label: 'Self Storage in Miami, FL',
		href: '/self-storage/florida/miami/',
	},
	{
		label: 'Self Storage in New York, NY',
		href: '/self-storage/new-york/new-york/',
	},
	{
		label: 'Self Storage in Seattle, WA',
		href: '/self-storage/washington/seattle/',
	},
];

const isDesktopViewport = () =>
	typeof window !== 'undefined' &&
	window.innerWidth >= breakpointsByName.spMedium;

const EMPTY_CITIES = [];

const selectPagesNearbyCities = (state) => state.pages?.content?.nearbyCities;
const selectAppNearbyCities = (state) => state.app?.nearbyCities;
const selectSearchDrillDownLinks = (state) => state.search?.drillDownLinks;
const selectFacilityDrillDownLinks = (state) =>
	state.facilities?.drillDownLinks;

export const selectMenuData = createSelector(
	[
		selectPagesNearbyCities,
		selectAppNearbyCities,
		selectSearchDrillDownLinks,
		selectFacilityDrillDownLinks,
	],
	(
		pagesNearbyCities,
		appNearbyCities,
		searchDrillDownLinks,
		facilityDrillDownLinks,
	) => ({
		nearbyCities: pagesNearbyCities || appNearbyCities || EMPTY_CITIES,
		drillDownLinks: searchDrillDownLinks ?? facilityDrillDownLinks ?? null,
	}),
);

export const Menu = ({
	findStorageShowing,
	onToggleFindStorage,
	onSetFindStorage,
	dropdownShowing,
	onToggleDropdown,
	onSetDropdown,
	ownersShowing,
	onToggleOwners,
	onSetOwners,
	onCloseNavigation,
}) => {
	const { nearbyCities, drillDownLinks } = useSelector(selectMenuData);

	const findStorageNearUserLinks = useMemo(
		() => getFindStorageNearUserLinks(nearbyCities),
		[nearbyCities],
	);

	const isDrillDownLinksAvailable = useMemo(
		() => isDrillDownLinksPresent(drillDownLinks),
		[drillDownLinks],
	);

	const closeOtherDropdowns = useCallback(
		(excluded) => {
			if (excluded !== 'findStorage') {
				onSetFindStorage(false);
			}
			if (excluded !== 'storageTypes') {
				onSetDropdown(false);
			}
			if (excluded !== 'owners') {
				onSetOwners(false);
			}
		},
		[onSetFindStorage, onSetDropdown, onSetOwners],
	);

	const handleFindStorageToggle = useCallback(
		(event) => {
			if (isDesktopViewport() && !findStorageShowing) {
				closeOtherDropdowns('findStorage');
			}
			onToggleFindStorage(event);
		},
		[findStorageShowing, onToggleFindStorage, closeOtherDropdowns],
	);

	const handleStorageTypesToggle = useCallback(
		(event) => {
			if (isDesktopViewport() && !dropdownShowing) {
				closeOtherDropdowns('storageTypes');
			}
			onToggleDropdown(event);
		},
		[dropdownShowing, onToggleDropdown, closeOtherDropdowns],
	);

	const handleOwnersToggle = useCallback(
		(event) => {
			if (isDesktopViewport() && !ownersShowing) {
				closeOtherDropdowns('owners');
			}
			onToggleOwners(event);
		},
		[ownersShowing, onToggleOwners, closeOtherDropdowns],
	);

	const handleClickOutside = useCallback(
		(event) => {
			const clickedElement = event.target;
			const isMenuOrToggleClick =
				!!clickedElement &&
				typeof clickedElement.closest === 'function' &&
				(!!clickedElement.closest('.nav-menu') ||
					!!clickedElement.closest('.nav-button') ||
					!!clickedElement.closest('.dropdown-toggle'));

			if (!isMenuOrToggleClick) {
				onSetFindStorage(false);
				onSetDropdown(false);
				onSetOwners(false);
				if (onCloseNavigation) {
					onCloseNavigation();
				}
			}
		},
		[onSetFindStorage, onSetDropdown, onSetOwners, onCloseNavigation],
	);

	useEffect(() => {
		on('mouseup', handleClickOutside);
		return () => {
			off('mouseup', handleClickOutside);
		};
	}, [handleClickOutside]);

	return (
		<div className="nav-menu">
			<ul className="menu-list">
				<li className="menu-item">
					<label
						className={classnames(
							'menu-link dropdown-toggle ss-link',
							{ open: findStorageShowing },
						)}
						htmlFor="find-storage-checkbox"
						aria-expanded={findStorageShowing}
						aria-haspopup="true"
					>
						<Checkbox
							onChange={handleFindStorageToggle}
							key="Find Storage Dropdown"
							id="find-storage-checkbox"
							name="find-storage-checkbox"
							className="visually-hidden"
							checked={findStorageShowing}
						/>
						Find Storage{' '}
						<CaretDown
							fill={
								findStorageShowing
									? COLOR_SECONDARY_BLUE
									: COLOR_BLUE
							}
						/>
					</label>
					<div
						className={classnames('find-storage-dropdown-menu', {
							showing: findStorageShowing,
						})}
					>
						<div className="find-storage-dropdown-content">
							<div className="find-storage-columns">
								{drillDownLinks && isDrillDownLinksAvailable ? (
									<GeoSpecificMenu
										geoLocation={drillDownLinks}
									/>
								) : (
									<>
										{nearbyCities?.length > 0 && (
											<div className="find-storage-column">
												<p className="find-storage-heading">
													Self Storage Near You:
												</p>
												{findStorageNearUserLinks.map(
													(cityLink) => (
														<Link
															key={cityLink.label}
															className="menu-link find-storage-link"
															href={cityLink.href}
															rel="noopener noreferrer"
														>
															{cityLink.label}
														</Link>
													),
												)}
											</div>
										)}

										<div className="find-storage-column">
											<p className="find-storage-heading">
												Self Storage in Top Cities:
											</p>
											{FIND_STORAGE_TOP_CITIES_LINKS.map(
												(cityLink) => (
													<Link
														key={cityLink.label}
														className="menu-link find-storage-link"
														href={cityLink.href}
														rel="noopener noreferrer"
													>
														{cityLink.label}
													</Link>
												),
											)}
										</div>
									</>
								)}
							</div>
							{!(drillDownLinks && isDrillDownLinksAvailable) && (
								<div className="find-storage-columns">
									<DefaultMenu />
								</div>
							)}
						</div>
						<div className="find-storage-cta-row find-storage-cta-row--desktop">
							<Link
								className="menu-link find-storage-cta"
								href="/storage-units-near-me"
								rel="noopener noreferrer"
							>
								Browse All Facilities Near Me
							</Link>
						</div>
					</div>
				</li>

				<li className="menu-item">
					{}
					<label
						className={classnames(
							'menu-link dropdown-toggle ss-link',
							{ open: dropdownShowing },
						)}
						htmlFor="storage-types-checkbox"
						aria-expanded={dropdownShowing}
						aria-haspopup="true"
					>
						<Checkbox
							onChange={handleStorageTypesToggle}
							key="Storage Types Dropdown"
							id="storage-types-checkbox"
							name="storage-types-checkbox"
							className="visually-hidden"
							checked={dropdownShowing}
						/>
						Storage Types{' '}
						<CaretDown
							fill={
								dropdownShowing
									? COLOR_SECONDARY_BLUE
									: COLOR_BLUE
							}
						/>
					</label>
					<div>
						<ul
							className={classnames('dropdown-menu', {
								showing: dropdownShowing,
							})}
						>
							<li className="menu-item">
								<Link
									className="menu-link"
									href="/self-storage-near-me/climate-controlled/"
									rel="noopener noreferrer"
								>
									Find self-storage
								</Link>
							</li>
							<li className="menu-item">
								<Link
									className="menu-link"
									href="/storage-types/vehicle-self-storage"
									rel="noopener noreferrer"
								>
									Vehicle Storage
								</Link>
							</li>
							<li className="menu-item">
								<Link
									className="menu-link"
									href="/storage-types/climate-controlled-self-storage"
									rel="noopener noreferrer"
								>
									Climate Controlled Storage
								</Link>
							</li>
						</ul>
					</div>
				</li>
				<li className="menu-item">
					<Link
						className="menu-link"
						href="/self-storage-sizes/"
						rel="noopener noreferrer"
					>
						Size Guide
					</Link>
				</li>
				<li className="menu-item">
					<Link
						className="menu-link"
						href="https://www.selfstorage.com/blog/"
						rel="noopener noreferrer"
					>
						Blog
					</Link>
				</li>
				<li className="menu-item hide-on-desktop">
					{}
					<label
						className={classnames(
							'menu-link dropdown-toggle owners-dropdown-menu ss-link',
							{ open: ownersShowing },
						)}
						htmlFor="owners-checkbox-mobile"
						aria-expanded={ownersShowing}
						aria-haspopup="true"
					>
						<Checkbox
							onChange={handleOwnersToggle}
							key="Owners Dropdown"
							id="owners-checkbox-mobile"
							name="owners-checkbox-mobile"
							className="visually-hidden"
							checked={ownersShowing}
						/>
						Owners{' '}
						<CaretDown
							fill={
								ownersShowing
									? COLOR_SECONDARY_BLUE
									: COLOR_BLUE
							}
						/>
					</label>
					<div>
						<ul
							className={classnames('dropdown-menu', {
								showing: ownersShowing,
							})}
						>
							<li className="menu-item">
								<Link
									className="menu-link"
									href="https://www.storable.com/products/marketplace/"
									rel="noopener noreferrer"
								>
									Add Your Facility
								</Link>
							</li>
							<li className="menu-item">
								<Link
									className="menu-link"
									href="https://argus-selfstorage.com/"
									rel="noopener noreferrer"
								>
									Buy & Sell Facilities
								</Link>
							</li>
						</ul>
					</div>
				</li>
				<li className="menu-item find-storage-mobile-cta-row">
					<Link
						className="menu-link find-storage-cta"
						href="/self-storage-near-me/climate-controlled/"
						rel="noopener noreferrer"
					>
						Browse All Facilities Near Me
					</Link>
				</li>
			</ul>
		</div>
	);
};

Menu.propTypes = {
	onToggleFindStorage: PropTypes.func.isRequired,
	onToggleDropdown: PropTypes.func.isRequired,
	onToggleOwners: PropTypes.func.isRequired,
	findStorageShowing: PropTypes.bool,
	dropdownShowing: PropTypes.bool,
	ownersShowing: PropTypes.bool,
	onSetFindStorage: PropTypes.func.isRequired,
	onSetDropdown: PropTypes.func.isRequired,
	onSetOwners: PropTypes.func.isRequired,
	onCloseNavigation: PropTypes.func,
};

export default enhance(Menu);
